import { create } from 'zustand';
import { DEFAULT_LANGUAGE, type Language } from '@models/i18n/language.model';
import type { NameCheckResponse } from '@models/landing/api/pnr-group-validation.model';
import { sessionStorageUtil } from '@utils/session-storage.util';
import { SESSION_KEYS } from '@utils/session-keys.constants';

export interface SavedContactDetails {
  countryCode: string;
  email: string;
  phone: string;
  useForAllPassengers?: boolean;
}

export type PassengerApisValues = Record<string, string>;
export type PassengerApisFieldErrors = Record<string, string>;

export interface PassengerEmergencyContactData {
  fullName: string;
  relationship: string;
  phone: string;
  countryCode: string;
  isPrimary: boolean;
  isPrimaryChecked: boolean;
  isSecondaryChecked: boolean;
  error: {
    fullName: string;
    phone: string;
  };
}

export interface LoadingErrorState {
  cmsKey: string | null;
  hasError: boolean;
  errorMessage: string | null;
}

export interface FlightTabItem {
  label: string;
  value: string;
  content: string;
  id: string;
}

export interface GroupBookingNameCheckPassenger {
  passengerId: number;
  fName: string;
  lName: string;
  docNumber: string;
  paxJourneyIDs: number[];
  overrideExistingSession: boolean;
  response: NameCheckResponse | null;
  passengerType?: number;
  error: string | null;
}

export type FlightTabItems = FlightTabItem[];

interface AppState {
  language: Language;
  isBootstrapped: boolean;
  isLoading: boolean;
  isLanguageLoading: boolean;
  decimalPlaces: number;
  fatalError: string | null;
  loadingError: LoadingErrorState;
  savedContactDetails: SavedContactDetails | null;
  selectedPaxForCheckIn: number[];
  currentStep: number;
  skywardNumbers: Record<number, string>;
  passengerApisValues: PassengerApisValues[];
  passengerApisFieldErrors: PassengerApisFieldErrors[];
  passengerEmergencyContactData: PassengerEmergencyContactData[];
  groupBookingNameCheckPassengers: GroupBookingNameCheckPassenger[];
  isWebSessionActionPopupOpen: boolean;
  webSessionSelectPaxRetryToken: number;
  showLandingHeaderStepper: boolean;
  showMainFooter: boolean;
  includeReviewBreadcrumb: boolean;
  // physicalFlightId: FlightTabItems | null;
  // setPhysicalFlightId: (flightId: FlightTabItems) => void;
}

interface AppActions {
  setLanguage: (language: Language) => void;
  setBootstrapped: (value: boolean) => void;
  setLoading: (value: boolean) => void;
  setLanguageLoading: (value: boolean) => void;
  setFatalError: (error: string | null) => void;
  setLoadingError: (value: LoadingErrorState) => void;
  setSavedContactDetails: (value: SavedContactDetails) => void;
  setSelectedPaxForCheckIn: (resPaxIds: number[]) => void;
  clearSelectedPaxForCheckIn: () => void;
  setCurrentStep: (step: number) => void;
  setSkywardNumber: (resPaxId: number, skywardNumber: string) => void;
  getSkywardNumber: (resPaxId: number) => string | undefined;
  clearSkywardNumber: (resPaxId: number) => void;
  clearAllSkywardNumbers: () => void;
  setPassengerApisValues: (index: number, values: PassengerApisValues) => void;
  setPassengerApisValue: (index: number, key: string, value: string) => void;
  clearPassengerApisValuesByIndex: (index: number) => void;
  clearPassengerApisValues: () => void;
  setPassengerApisFieldErrors: (index: number, errors: PassengerApisFieldErrors) => void;
  clearPassengerApisFieldErrors: () => void;
  setPassengerEmergencyContactData: (data: PassengerEmergencyContactData[]) => void;
  clearPassengerEmergencyContactDataByIndex: (index: number) => void;
  clearPassengerEmergencyContactData: () => void;
  setGroupBookingNameCheckPassengers: (value: GroupBookingNameCheckPassenger[]) => void;
  setWebSessionActionPopupOpen: (open: boolean) => void;
  triggerWebSessionSelectPaxRetry: () => void;
  clearWebSessionSelectPaxRetry: () => void;
  setShowLandingHeaderStepper: (show: boolean) => void;
  setShowMainFooter: (show: boolean) => void;
  setIncludeReviewBreadcrumb: (value: boolean) => void;
}

type AppStore = AppState & AppActions;

const resolveInitialLanguage = (): Language => {
  return sessionStorageUtil.get<Language>(SESSION_KEYS.LANGUAGE) ?? DEFAULT_LANGUAGE;
};

const resolveInitialSelectedPax = (): number[] => {
  return sessionStorageUtil.get<number[]>(SESSION_KEYS.SELECTED_PAX_FOR_CHECK_IN) ?? [];
};

const resolveInitialShowLandingHeaderStepper = (): boolean => {
  return sessionStorageUtil.get<boolean>(SESSION_KEYS.SHOW_LANDING_HEADER_STEPPER) ?? false;
};

const resolveInitialIncludeReviewBreadcrumb = (): boolean => {
  return sessionStorageUtil.get<boolean>(SESSION_KEYS.INCLUDE_REVIEW_BREADCRUMB) ?? false;
};

const createPassengerApisActions = (
  set: (partial: Partial<AppStore> | ((state: AppStore) => Partial<AppStore>)) => void,
): Pick<
  AppActions,
  | 'setPassengerApisValues'
  | 'setPassengerApisValue'
  | 'clearPassengerApisValuesByIndex'
  | 'clearPassengerApisValues'
  | 'setPassengerApisFieldErrors'
  | 'clearPassengerApisFieldErrors'
  | 'setGroupBookingNameCheckPassengers'
> => ({
  setPassengerApisValues: (index, values): void => {
    set((state) => {
      const nextPassengerApisValues = [...state.passengerApisValues];
      const passengerValues = nextPassengerApisValues[index] ?? {};

      nextPassengerApisValues[index] = {
        ...values,
        ...passengerValues,
      };

      return { passengerApisValues: nextPassengerApisValues };
    });
  },
  setPassengerApisValue: (index, key, value): void => {
    set((state) => {
      const nextPassengerApisValues = [...state.passengerApisValues];
      const passengerValues = nextPassengerApisValues[index] ?? {};

      nextPassengerApisValues[index] = {
        ...passengerValues,
        [key]: value,
      };

      return { passengerApisValues: nextPassengerApisValues };
    });
  },
  clearPassengerApisValuesByIndex: (index): void => {
    set((state) => {
      const nextPassengerApisValues = [...state.passengerApisValues];
      nextPassengerApisValues[index] = {};

      return { passengerApisValues: nextPassengerApisValues };
    });
  },
  clearPassengerApisValues: (): void => {
    set({ passengerApisValues: [] });
  },
  setPassengerApisFieldErrors: (index, errors): void => {
    set((state) => {
      const nextPassengerApisFieldErrors = [...state.passengerApisFieldErrors];
      nextPassengerApisFieldErrors[index] = errors;
      return { passengerApisFieldErrors: nextPassengerApisFieldErrors };
    });
  },
  clearPassengerApisFieldErrors: (): void => {
    set({ passengerApisFieldErrors: [] });
  },
  setGroupBookingNameCheckPassengers: (value): void => {
    set({ groupBookingNameCheckPassengers: value });
  },
});

const createPassengerEmergencyContactActions = (
  set: (partial: Partial<AppStore> | ((state: AppStore) => Partial<AppStore>)) => void,
): Pick<
  AppActions,
  | 'setPassengerEmergencyContactData'
  | 'clearPassengerEmergencyContactDataByIndex'
  | 'clearPassengerEmergencyContactData'
> => ({
  setPassengerEmergencyContactData: (data): void => {
    set({ passengerEmergencyContactData: data });
  },
  clearPassengerEmergencyContactDataByIndex: (index): void => {
    set((state) => {
      const nextPassengerEmergencyContactData = [...state.passengerEmergencyContactData];
      nextPassengerEmergencyContactData[index] = {
        fullName: '',
        relationship: '',
        phone: '',
        countryCode: '',
        isPrimary: false,
        isPrimaryChecked: false,
        isSecondaryChecked: false,
        error: {
          fullName: '',
          phone: '',
        },
      };

      return { passengerEmergencyContactData: nextPassengerEmergencyContactData };
    });
  },
  clearPassengerEmergencyContactData: (): void => {
    set({ passengerEmergencyContactData: [] });
  },
});

const createSkywardNumberActions = (
  set: (partial: Partial<AppStore> | ((state: AppStore) => Partial<AppStore>)) => void,
  get: () => AppStore,
): Pick<
  AppActions,
  'setSkywardNumber' | 'getSkywardNumber' | 'clearSkywardNumber' | 'clearAllSkywardNumbers'
> => ({
  setSkywardNumber: (resPaxId, skywardNumber): void => {
    set((state) => ({
      skywardNumbers: {
        ...state.skywardNumbers,
        [resPaxId]: skywardNumber,
      },
    }));
  },
  getSkywardNumber: (resPaxId): string | undefined => {
    return get().skywardNumbers[resPaxId];
  },
  clearSkywardNumber: (resPaxId): void => {
    set((state) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [resPaxId]: _removed, ...rest } = state.skywardNumbers;
      return { skywardNumbers: rest };
    });
  },
  clearAllSkywardNumbers: (): void => {
    set({ skywardNumbers: {} });
  },
});

const createStateActions = (
  set: (partial: Partial<AppStore> | ((state: AppStore) => Partial<AppStore>)) => void,
  get: () => AppStore,
): Omit<AppStore, keyof AppState> => ({
  triggerWebSessionSelectPaxRetry: (): void => {
    set((state) => ({ webSessionSelectPaxRetryToken: state.webSessionSelectPaxRetryToken + 1 }));
  },
  clearWebSessionSelectPaxRetry: (): void => {
    set({ webSessionSelectPaxRetryToken: 0 });
  },
  setShowLandingHeaderStepper: (show): void => {
    sessionStorageUtil.set(SESSION_KEYS.SHOW_LANDING_HEADER_STEPPER, show);
    set({ showLandingHeaderStepper: show });
  },
  setShowMainFooter: (show): void => set({ showMainFooter: show }),
  setIncludeReviewBreadcrumb: (value): void => {
    sessionStorageUtil.set(SESSION_KEYS.INCLUDE_REVIEW_BREADCRUMB, value);
    set({ includeReviewBreadcrumb: value });
  },
  setWebSessionActionPopupOpen: (open: boolean): void => set({ isWebSessionActionPopupOpen: open }),
  setLanguage: (language): void => {
    sessionStorageUtil.set(SESSION_KEYS.LANGUAGE, language);
    set({ language });
  },
  setBootstrapped: (value): void => set({ isBootstrapped: value }),
  setLoading: (value): void => set({ isLoading: value }),
  setLanguageLoading: (value): void => set({ isLanguageLoading: value }),
  setFatalError: (error): void => set({ fatalError: error }),
  setLoadingError: (value): void => set({ loadingError: value }),
  setSavedContactDetails: (value): void => set({ savedContactDetails: value }),
  setSelectedPaxForCheckIn: (resPaxIds): void => {
    sessionStorageUtil.set(SESSION_KEYS.SELECTED_PAX_FOR_CHECK_IN, resPaxIds);
    set({ selectedPaxForCheckIn: resPaxIds });
  },
  clearSelectedPaxForCheckIn: (): void => {
    sessionStorageUtil.remove(SESSION_KEYS.SELECTED_PAX_FOR_CHECK_IN);
    set({ selectedPaxForCheckIn: [] });
  },
  setCurrentStep: (step): void => set({ currentStep: step }),
  ...createSkywardNumberActions(set, get),
  ...createPassengerApisActions(set),
  ...createPassengerEmergencyContactActions(set),
});

const createAppStore = (
  set: (partial: Partial<AppStore> | ((state: AppStore) => Partial<AppStore>)) => void,
  get: () => AppStore,
): AppStore => ({
  language: resolveInitialLanguage(),
  isBootstrapped: false,
  isLoading: true,
  isLanguageLoading: false,
  fatalError: null,
  loadingError: {
    cmsKey: null,
    hasError: false,
    errorMessage: null,
  },
  // physicalFlightId: null,
  // setPhysicalFlightId: (flightId) => set({ physicalFlightId: flightId }),
  savedContactDetails: null,
  selectedPaxForCheckIn: resolveInitialSelectedPax(),
  currentStep: 1,
  skywardNumbers: {},
  passengerApisValues: [],
  passengerApisFieldErrors: [],
  passengerEmergencyContactData: [],
  groupBookingNameCheckPassengers: [],
  isWebSessionActionPopupOpen: false,
  webSessionSelectPaxRetryToken: 0,
  showLandingHeaderStepper: resolveInitialShowLandingHeaderStepper(),
  showMainFooter: true,
  includeReviewBreadcrumb: resolveInitialIncludeReviewBreadcrumb(),
  decimalPlaces: 3,
  ...createStateActions(set, get),
});

export const useAppStore = create<AppStore>(createAppStore);
export const selectDecimalPlaces = (state: AppStore): number => state.decimalPlaces;
export const selectLanguage = (state: AppStore): Language => state.language;
export const selectIsBootstrapped = (state: AppStore): boolean => state.isBootstrapped;
export const selectIsLoading = (state: AppStore): boolean => state.isLoading;
export const selectIsLanguageLoading = (state: AppStore): boolean => state.isLanguageLoading;
export const selectFatalError = (state: AppStore): string | null => state.fatalError;
export const selectLoadingError = (state: AppStore): LoadingErrorState => state.loadingError;
export const selectSavedContactDetails = (state: AppStore): SavedContactDetails | null =>
  state.savedContactDetails;
export const selectSelectedPaxForCheckIn = (state: AppStore): number[] =>
  state.selectedPaxForCheckIn;
export const selectCurrentStep = (state: AppStore): number => state.currentStep;
export const selectIncludeReviewBreadcrumb = (state: AppStore): boolean =>
  state.includeReviewBreadcrumb;
export const selectSkywardNumber =
  (resPaxId: number) =>
  (state: AppStore): string | undefined =>
    state.skywardNumbers[resPaxId];
export const selectAllSkywardNumbers = (state: AppStore): Record<number, string> =>
  state.skywardNumbers;
export const selectPassengerApisValuesByIndex =
  (index: number) =>
  (state: AppStore): PassengerApisValues =>
    state.passengerApisValues[index] ?? {};
export const selectSetPassengerApisValue = (state: AppStore): AppActions['setPassengerApisValue'] =>
  state.setPassengerApisValue;
export const selectSetPassengerApisValues = (
  state: AppStore,
): AppActions['setPassengerApisValues'] => state.setPassengerApisValues;
export const selectClearPassengerApisValuesByIndex = (
  state: AppStore,
): AppActions['clearPassengerApisValuesByIndex'] => state.clearPassengerApisValuesByIndex;
export const selectPassengerApisFieldErrorsByIndex =
  (index: number) =>
  (state: AppStore): PassengerApisFieldErrors =>
    state.passengerApisFieldErrors[index] ?? {};
export const selectPassengerApisFieldErrors = (state: AppStore): PassengerApisFieldErrors[] =>
  state.passengerApisFieldErrors;
export const selectSetPassengerApisFieldErrors = (
  state: AppStore,
): AppActions['setPassengerApisFieldErrors'] => state.setPassengerApisFieldErrors;
export const selectPassengerEmergencyContactData = (
  state: AppStore,
): PassengerEmergencyContactData[] => state.passengerEmergencyContactData;
export const selectGroupBookingNameCheckPassengers = (
  state: AppStore,
): GroupBookingNameCheckPassenger[] => state.groupBookingNameCheckPassengers;
export const selectSetGroupBookingNameCheckPassengers = (
  state: AppStore,
): AppActions['setGroupBookingNameCheckPassengers'] => state.setGroupBookingNameCheckPassengers;
export const selectSetPassengerEmergencyContactData = (
  state: AppStore,
): AppActions['setPassengerEmergencyContactData'] => state.setPassengerEmergencyContactData;
export const selectClearPassengerEmergencyContactDataByIndex = (
  state: AppStore,
): AppActions['clearPassengerEmergencyContactDataByIndex'] =>
  state.clearPassengerEmergencyContactDataByIndex;
export const selectWebSessionSelectPaxRetryToken = (state: AppStore): number =>
  state.webSessionSelectPaxRetryToken;
export const selectShowLandingHeaderStepper = (state: AppStore): boolean =>
  state.showLandingHeaderStepper;
export const selectShowMainFooter = (state: AppStore): boolean => state.showMainFooter;
export const selectSetShowMainFooter = (state: AppStore): AppActions['setShowMainFooter'] =>
  state.setShowMainFooter;
export const selectSetIncludeReviewBreadcrumb = (
  state: AppStore,
): AppActions['setIncludeReviewBreadcrumb'] => state.setIncludeReviewBreadcrumb;
export const selectSetShowLandingHeaderStepper = (
  state: AppStore,
): AppActions['setShowLandingHeaderStepper'] => state.setShowLandingHeaderStepper;
